import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Users, Download, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Subscribers = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
  });

  const fetchSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/newsletter/subscribers`, getAuthHeader());
      setSubscribers(response.data);
    } catch (error) {
      toast.error("Failed to fetch subscribers");
    } finally {
      setIsLoading(false);
    }
  };

  const exportCSV = () => {
    if (subscribers.length === 0) {
      toast.error("No subscribers to export");
      return;
    }

    const headers = ["Email", "Subscribed At"];
    const rows = subscribers.map(sub => [
      sub.email,
      new Date(sub.subscribed_at).toLocaleDateString()
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subscribers_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Subscribers exported");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  return (
    <div data-testid="subscribers-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-[#1A2323] mb-2">Newsletter Subscribers</h1>
          <p className="text-[#4A5555]">People who subscribed to your newsletter</p>
        </div>
        <Button 
          variant="outline"
          className="border-[#2E8B8B] text-[#2E8B8B] hover:bg-[#2E8B8B]/10"
          onClick={exportCSV}
          disabled={subscribers.length === 0}
          data-testid="export-csv-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Stats Card */}
      <Card className="border-[#E5E0D6] mb-6">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#2E8B8B] flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-3xl font-serif font-semibold text-[#1A2323]">
                {isLoading ? "..." : subscribers.length}
              </p>
              <p className="text-sm text-[#4A5555]">Total Subscribers</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscribers Table */}
      <Card className="border-[#E5E0D6]">
        <CardHeader>
          <CardTitle className="font-serif text-lg text-[#1A2323]">All Subscribers</CardTitle>
          <CardDescription>View and manage your newsletter subscribers</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#2E8B8B]" />
            </div>
          ) : subscribers.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-12 h-12 text-[#E67E22] mx-auto mb-4 opacity-50" />
              <p className="text-[#4A5555]">No subscribers yet.</p>
              <p className="text-sm text-[#4A5555] mt-1">
                People can subscribe through the newsletter form on your landing page.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F2EB]">
                  <TableHead className="font-semibold text-[#1A2323]">#</TableHead>
                  <TableHead className="font-semibold text-[#1A2323]">Email</TableHead>
                  <TableHead className="font-semibold text-[#1A2323]">Subscribed</TableHead>
                  <TableHead className="font-semibold text-[#1A2323]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscribers.map((subscriber, index) => (
                  <TableRow key={subscriber.id} data-testid={`subscriber-row-${subscriber.id}`}>
                    <TableCell className="font-medium text-[#4A5555]">{index + 1}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#E67E22]/10 text-[#E67E22] flex items-center justify-center font-medium text-sm">
                          {subscriber.email?.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-[#1A2323]">{subscriber.email}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-[#4A5555]">
                      {formatDate(subscriber.subscribed_at)}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        subscriber.is_active 
                          ? "bg-[#2E8B8B]/10 text-[#2E8B8B]" 
                          : "bg-[#4A5555]/10 text-[#4A5555]"
                      }`}>
                        {subscriber.is_active ? "Active" : "Inactive"}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Subscribers;
